import { AlertTriangleIcon } from 'lucide-react'
import { Button } from '@app/components/ui/button'
import { Card, CardContent, CardHeader } from '@app/components/ui/card'
import { cn } from '@app/lib/cn'
import { statGridClass, tileSpanClass } from './stat-tiles'

/** Skeleton block — holds the layout's place while loading, no jump on load. */
function SkeletonBlock({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-xs bg-hairline/70', className)} />
}

/**
 * `tileCount` mirrors what StatTiles will draw once the answer arrives: six
 * tiles for an admin, five for a `purchasing` account, four for a `staff` one
 * whose payload carries no money blocks at all. The screen works it out from
 * the session (the role is known before the request finishes), purely so the
 * tiles do not reflow the moment the data lands — which is also why the grid
 * classes come from StatTiles itself rather than being restated here.
 */
export function DashboardSkeleton({ tileCount }: { tileCount: number }) {
  const tiles = Array.from({ length: tileCount }, (_, i) => i)
  const span = tileSpanClass(tileCount)

  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-live="polite">
      <span className="sr-only">Panel verileri yükleniyor…</span>

      <div className={statGridClass(tileCount)}>
        {tiles.map((i) => (
          <div
            key={i}
            className={cn(
              'rounded-sm border border-hairline bg-surface px-3.5 py-3',
              // The odd tile out takes the leftover columns, exactly as the
              // money tile it stands in for will.
              i === tiles.length - 1 ? span : undefined,
            )}
          >
            <SkeletonBlock className="h-3 w-24" />
            <SkeletonBlock className="mt-3 h-9 w-28 lg:h-11" />
            <SkeletonBlock className="mt-3 h-3 w-full" />
          </div>
        ))}
      </div>

      <div className="grid gap-3 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader>
            <SkeletonBlock className="h-4 w-32" />
          </CardHeader>
          <CardContent className="flex flex-col gap-2.5">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <SkeletonBlock key={i} className="h-5 w-full" />
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <SkeletonBlock className="h-4 w-28" />
          </CardHeader>
          <CardContent>
            <SkeletonBlock className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-3 xl:grid-cols-2">
        {[0, 1].map((i) => (
          <Card key={i}>
            <CardHeader>
              <SkeletonBlock className="h-4 w-36" />
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              {[0, 1, 2, 3, 4].map((j) => (
                <SkeletonBlock key={j} className="h-6 w-full" />
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

export function DashboardError({
  message,
  onRetry,
  refreshing,
}: {
  message: string
  onRetry: () => void
  refreshing: boolean
}) {
  return (
    <Card className="mx-auto max-w-lg">
      <CardContent className="py-8 text-center">
        <AlertTriangleIcon className="mx-auto size-5 text-hardal" aria-hidden />
        <h3 className="mt-2 font-display text-base font-semibold tracking-tight text-ink">
          Panel verileri alınamadı
        </h3>
        <p className="mt-1 text-data text-ink-muted">{message}</p>
        <Button variant="primary" className="mt-4" onClick={onRetry} disabled={refreshing}>
          {refreshing ? 'Yeniden deneniyor…' : 'Yeniden dene'}
        </Button>
      </CardContent>
    </Card>
  )
}
